import React, { useState } from "react";
import { Formik, FieldArray } from "formik";
import Swal from "sweetalert2";
import Breadcrums from "../../../common/breadcrumbs";
import { handleServerValidations } from "../../../../../utils/commonfunction";
import { SWAL_SETTINGS } from "../../../../../utils/Constants";
import { useTranslation } from "react-i18next";
import * as Yup from 'yup';
import * as colorPaletteService from "../../../services/colorPaletteService";
import { useNavigate } from "react-router-dom";

const AddColors = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [submitClicked, setSubmitClicked] = useState(false);

    const breadcrumbs = [
        { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
        {
            title: "Color Palettes",
            url: "/admin/productManagement/colors/list/1",
        },
        { title: t("btn_add_new"), url: "" },
    ];

    const validationSchema = Yup.object().shape({
        name: Yup.string()
            .trim()
            .required("Palette name is required")
            .max(100, "Palette name must be less than 100 characters"),
        colors: Yup.array().of(
            Yup.object().shape({
                name: Yup.string().trim().required("Color name is required"),
                hex: Yup.string().trim().required("Hex code is required")
            })
        ).min(1, "At least one color is required in the palette")
    });

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <Formik
                validateOnChange={true}
                initialValues={{
                    name: "",
                    colors: [{ name: "", hex: "#000000" }]
                }}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                    setSubmitClicked(true);
                    const payload = {
                        name: values.name,
                        colors: JSON.stringify(values.colors)
                    };

                    colorPaletteService.Add(payload).then((response) => {
                        setSubmitClicked(false);
                        if (response.success) {
                            Swal.fire({
                                icon: "success",
                                text: response.message,
                                ...SWAL_SETTINGS,
                            });
                            setTimeout(() => {
                                navigate("/admin/productManagement/colors/list/1");
                            }, 500);
                        } else {
                            Swal.fire({
                                icon: "error",
                                text: handleServerValidations(response),
                                ...SWAL_SETTINGS,
                            });
                        }
                    }).catch((err) => {
                        setSubmitClicked(false);
                        Swal.fire({
                            icon: "error",
                            text: "Error creating color palette",
                            ...SWAL_SETTINGS,
                        });
                    });
                }}
            >
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
                    <form onSubmit={handleSubmit} noValidate>
                        <div className="row row-sm">
                            <div className="col-lg-12 col-md-12 animation_fade">
                                <div className="card custom-card">
                                    <div className="card-body">
                                        <h6 className="main-content-label mb-3">Add Color Palette</h6>

                                        <div className="row row-sm">
                                            <div className="col-md-12 text-center form-group">
                                                <label htmlFor="name" className="text-left d-flex">
                                                    Palette Name: <span className="requirestar">*</span>
                                                </label>
                                                <input
                                                    name="name"
                                                    id="name"
                                                    type="text"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.name}
                                                    className="form-control"
                                                    placeholder="Enter palette name (e.g. Earth, Red, Nudes)"
                                                />
                                                <span className="text-danger d-flex text-left">
                                                    {errors.name && touched.name && errors.name}
                                                </span>
                                            </div>

                                            <div className="col-md-12 text-center form-group mt-3">
                                                <label className="text-left d-flex font-weight-bold">
                                                    Palette Colors: <span className="requirestar">*</span>
                                                </label>
                                                <FieldArray
                                                    name="colors"
                                                    render={(arrayHelpers) => (
                                                        <div>
                                                            {values.colors && values.colors.length > 0 ? (
                                                                values.colors.map((color, index) => (
                                                                    <div key={index} className="d-flex align-items-center mb-3">
                                                                        <input
                                                                            name={`colors.${index}.name`}
                                                                            value={color.name}
                                                                            onChange={handleChange}
                                                                            className="form-control me-2"
                                                                            placeholder={`Color Name (e.g. Maroon)`}
                                                                            style={{ flex: 2 }}
                                                                        />
                                                                        <input
                                                                            type="color"
                                                                            name={`colors.${index}.hex`}
                                                                            value={color.hex}
                                                                            onChange={(e) => setFieldValue(`colors.${index}.hex`, e.target.value)}
                                                                            className="form-control form-control-color me-2 cp"
                                                                            style={{ width: "60px", padding: "2px", height: "38px" }}
                                                                            title="Choose hex code"
                                                                        />
                                                                        <input
                                                                            type="text"
                                                                            name={`colors.${index}.hex_text`}
                                                                            value={color.hex}
                                                                            onChange={(e) => setFieldValue(`colors.${index}.hex`, e.target.value)}
                                                                            className="form-control me-2"
                                                                            placeholder="#FFFFFF"
                                                                            style={{ flex: 1 }}
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-danger"
                                                                            onClick={() => arrayHelpers.remove(index)}
                                                                            disabled={values.colors.length === 1}
                                                                        >
                                                                            <i className="ri-delete-bin-line"></i>
                                                                        </button>
                                                                    </div>
                                                                ))
                                                            ) : null}
                                                            <div className="d-flex align-items-center gap-2 mt-2">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-main-primary signbtn d-flex text-xs"
                                                                    onClick={() => arrayHelpers.push({ name: "", hex: "#000000" })}
                                                                >
                                                                    + Add Color
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                />
                                                <span className="text-danger d-flex text-left">
                                                    {typeof errors.colors === 'string' && errors.colors}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="mt-4">
                                            <button
                                                className="btn btn-main-primary signbtn mr-2"
                                                type="submit"
                                                disabled={submitClicked}
                                            >
                                                <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                                {t("btn_submit")}
                                            </button>
                                            <button
                                                className="btn ripple btn-dark"
                                                type="button"
                                                onClick={() => navigate("/admin/productManagement/colors/list/1")}
                                            >
                                                <i className="ace-icon-solid ion-arrow-return-left bigger-110 mx-1"></i>
                                                {t("btn_back")}
                                            </button>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                )}
            </Formik>
        </>
    );
};

export default AddColors;
